<script setup lang="ts">
import api from '@/api';
import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import { getFullName } from '@/helpers/format';
import { findHousehold, householdLabel, resolveHouseholds } from '@/helpers/household';
import type { Household } from '@/helpers/household';
import { useRequestIncomeExpenseProcess } from '@/helpers/useRequestIncomeExpenseProcess';
import type { ApplicantFinancials, ApplicantId } from '@/types';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep } from 'lodash-es';
import { onBeforeMount, ref } from 'vue';
import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
import { createFinancials } from '@/helpers/builder';
const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack } = useRequestIncomeExpenseProcess();

const isReady = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);

const isSecondaryDeclaration = false;

// An applicant who has already submitted (declared) is locked — view-only for everyone,
// including a partner re-opening the household after the other member submitted.
const hasSubmitted = (applicantId: ApplicantId): boolean =>
  (loanApp.value.declaredApplicantIds ?? []).includes(applicantId);

// Which member's record actually holds the household's expenses. This decides household ownership,
// so it must read the persisted data rather than declaration state — a partner signing their
// declaration does not move the record.
const holdsExpenseRecord = (applicantId: ApplicantId): boolean =>
  loanApp.value.financials[applicantId]?.expenses != null;

// Expenses are declared once per household. An application can hold several households — partners
// can sit under different entities (a director married to a trustee), so households are resolved
// from marriage rows rather than from applicant type or borrower entity.
const households = resolveHouseholds(loanApp.value.applicants, holdsExpenseRecord);
const currentHousehold = findHousehold(authorisedUser.id, households);
const partnerId = currentHousehold?.memberIds.find((id) => id !== authorisedUser.id);

// One card per household: the broker completes every household, everyone else completes only their
// own. Expenses are not grouped by borrower entity — a household can span a company and a trust, so
// an entity heading would attribute a shared household's expenses to one member's company.
const ownHousehold: Household = currentHousehold ?? {
  ownerId: authorisedUser.id as ApplicantId,
  memberIds: [authorisedUser.id as ApplicantId],
};
const visibleHouseholds = loanApp.value.userType === 'Broker' ? households : [ownHousehold];

// A household is submitted as a unit (one member declares for both), so once either member has
// declared the whole household is locked — view-only for the partner logging in after.
const householdSubmitted = (applicantId: ApplicantId): boolean =>
  (findHousehold(applicantId, households)?.memberIds ?? [applicantId]).some(hasSubmitted);

const displayName = (applicantId: string): string =>
  loanApplicants.value[applicantId]?.preferredName || loanApplicants.value[applicantId]?.firstName || '';

const fullName = (applicantId: string): string => getFullName(loanApplicants.value[applicantId]);

// Applicants see a single card for their own household and the guidance above already names who it
// covers, so it needs no label. A broker sees every household on the loan and gets no guidance copy,
// so their cards are named by their members to tell them apart.
const showHouseholdLabel = loanApp.value.userType === 'Broker';

const labelFor = (household: Household): string => householdLabel(household, fullName);

const partnerName = partnerId ? displayName(partnerId) : '';

// Applicants outside the logged-in user's household declare their own expenses via the email
// request — named so the guidance never attributes a household to the wrong person.
const otherApplicantNames = households
  .filter((household) => household !== currentHousehold)
  .flatMap((household) => household.memberIds)
  .map(displayName)
  .filter(Boolean);

const canEditApplicantExpenses = (applicantId: ApplicantId): boolean => {
  if (loanApp.value.userType === 'Broker') return true;
  // Only the logged-in user's own household — never another household's expenses.
  const household = currentHousehold?.memberIds ?? [authorisedUser.id];
  if (!household.includes(applicantId)) return false;
  return !householdSubmitted(applicantId);
};

const initFinancials = visibleHouseholds.reduce((obj, household) => {
  obj[household.ownerId] = createFinancials();
  return obj;
}, {} as ApplicantFinancials);

const financials = ref({ ...initFinancials, ...cloneDeep(loanApp.value.financials) });

// Render order of the editable forms only — expense refs are collected in this order (a locked
// household renders no form), so validation indexes against it.
const editableApplicants = visibleHouseholds
  .map((household) => household.ownerId)
  .filter((ownerId) => canEditApplicantExpenses(ownerId));

const expenseRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();
const activeTab = ref<ApplicantId | undefined>(editableApplicants.length === 1 ? editableApplicants[0] : undefined);

const isCompleted = (id: ApplicantId) => financials.value[id].expenses?.numberOfDependants !== undefined;

const validateExpenses = async () => {
  // A secondary reviewing an already-declared household has nothing of their own to validate.
  if (isSecondaryDeclaration) return true;

  let isValid = true;
  const refCount = expenseRefs.value.length;
  for (let i = 0; i < refCount; i++) {
    isValid = (await expenseRefs.value[i].update()) ?? false;
    if (!isValid) {
      activeTab.value = editableApplicants[i];
      window.scrollTo(0, 0);
      break;
    }
  }
  return isValid;
};

const saveAndContinue = async () => {
  if (await validateExpenses()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};

onBeforeMount(async () => {
  isReady.value = false;
  try {
    const expenseTypesResponse = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    if (expenseTypesResponse.expenseTypes?.length) {
      expenseTypesResponse.expenseTypes.sort((a, b) => {
        const sortOrderA = a.sortOrder || 0;
        const sortOrderB = b.sortOrder || 0;
        return sortOrderA - sortOrderB;
      });
      expenseTypes.value = expenseTypesResponse.expenseTypes;
    }
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div v-if="isReady" class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What are your household
        <BxHighlight>expenses</BxHighlight>?
      </h2>
    </div>
    <div v-if="loanApp.applicants.length > 1 && loanApp.userType !== 'Broker'" class="my-6">
      <p v-if="isSecondaryDeclaration && partnerName">
        Please review your combined household expenses with {{ partnerName }}. If you wish to make any changes, please
        contact us.
      </p>
      <p v-else-if="partnerName">Please include your combined household expenses including {{ partnerName }}.</p>
      <p v-else-if="loanApp.actionType === 'createNew' && otherApplicantNames.length">
        Only include your expenses, we will request this information from {{ otherApplicantNames.join(', ') }} via
        email.
      </p>
    </div>

    <div class="my-6 grid gap-6">
      <div v-for="household in visibleHouseholds" :key="household.ownerId">
        <BxCard v-if="canEditApplicantExpenses(household.ownerId)" :class="household.ownerId !== activeTab && 'hidden'">
          <h2 v-if="showHouseholdLabel" class="mb-1 text-xl font-medium">{{ labelFor(household) }}</h2>
          <ExpensesForm
            :ref="expenseRefs.set"
            v-model="financials[household.ownerId].expenses"
            :applicant-id="household.ownerId"
            :animate="household.ownerId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="visibleHouseholds.length >= 1"
            :disabled="isSecondaryDeclaration"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>
        <BxCard :class="household.ownerId === activeTab && 'hidden'">
          <div class="flex items-center">
            <div class="card-details">
              <div class="font-medium">
                <span v-if="showHouseholdLabel">{{ labelFor(household) }}</span>
                <span :class="showHouseholdLabel && 'ml-6'" class="font-normal text-gray-400"
                  >({{ isCompleted(household.ownerId) ? 'completed' : 'incomplete' }})</span
                >
              </div>
            </div>
            <div v-if="canEditApplicantExpenses(household.ownerId)" class="card-button">
              <BxButton
                variant="secondary"
                :disabled="activeTab && activeTab !== household.ownerId"
                @click="activeTab = household.ownerId"
              >
                {{ isCompleted(household.ownerId) ? 'Edit' : 'Add' }} expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-details {
  flex-grow: 1;
}

.card-button {
  flex-basis: 200px;

  text-align: right;
}
</style>
